import * as React from "react";
import {
  Box,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import { useTheme } from "@mui/material/styles";

export interface AIAgentProcessSignal {
  tool: string;
  process_name: string;
  path: string;
}

export interface AIAgentVsCodeSignal {
  tool: string;
  home: string;
  versions: string[];
  latest_version: string | null;
}

export interface AIAgentJetBrainsSignal {
  tool: string;
  ide: string;
  home: string;
  plugin_dirs: string[];
}

export interface AIAgentConfigDirSignal {
  label: string;
  path: string;
}

export interface AIAgentBrowserExtensionSignal {
  browser: string;
  id: string;
  label: string;
  profile: string;
}

export interface AIAgentDnsSignal {
  domain: string;
}

export interface AIAgentUwpSignal {
  tool: string;
  package_id: string;
  version: string;
}

export interface AIAgentSignals {
  processes: AIAgentProcessSignal[];
  vscode_extensions: AIAgentVsCodeSignal[];
  jetbrains_plugins: AIAgentJetBrainsSignal[];
  config_dirs: AIAgentConfigDirSignal[];
  browser_extensions: AIAgentBrowserExtensionSignal[];
  dns_cache: AIAgentDnsSignal[];
  builtin_ai_uwp: AIAgentUwpSignal[];
}

export interface AIAgentSummary {
  software_count: number;
  signal_count: number;
}

interface AIAgentSoftwareItem {
  name: string;
  version: string;
}

interface AIAgentSignalsPanelProps {
  summary: AIAgentSummary;
  signals: AIAgentSignals;
  aiSoftware?: AIAgentSoftwareItem[];
}

const StatPill: React.FC<{ label: string; count: number; isDarkMode: boolean }> = ({
  label,
  count,
  isDarkMode,
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
      px: 1.5,
      py: 0.75,
      borderRadius: "8px",
      backgroundColor: isDarkMode ? "rgba(59,130,246,0.12)" : "#EFF4FF",
      border: `1px solid ${isDarkMode ? "rgba(59,130,246,0.35)" : "#D6E4FF"}`,
    }}
  >
    <Box
      sx={{
        minWidth: 22,
        height: 22,
        px: 0.75,
        borderRadius: "999px",
        backgroundColor: isDarkMode ? "#3B82F6" : "#4A7BF7",
        color: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "12px",
        fontWeight: 700,
      }}
    >
      {count}
    </Box>
    <Typography
      sx={{
        fontSize: "13px",
        fontWeight: 500,
        color: isDarkMode ? "#E5E7EB" : "#344054",
      }}
    >
      {label}
    </Typography>
  </Box>
);

interface SignalSection {
  key: string;
  title: string;
  count: number;
  render: () => React.ReactNode;
}

const AIAgentSignalsPanel: React.FC<AIAgentSignalsPanelProps> = ({
  summary,
  signals,
  aiSoftware = [],
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const totalDetections = (summary?.software_count || 0) + (summary?.signal_count || 0);

  const chipSx = {
    fontSize: "12px",
    height: "26px",
    borderRadius: "6px",
    backgroundColor: isDarkMode ? "rgba(255,255,255,0.06)" : "#F2F4F7",
    color: isDarkMode ? "#E5E7EB" : "#344054",
  };

  const rowText = {
    fontSize: "13px",
    color: isDarkMode ? "#D1D5DB" : "#475467",
  };

  const sections: SignalSection[] = [
    {
      key: "ai_software",
      title: "AI-related installed software",
      count: aiSoftware.length,
      render: () => (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {aiSoftware.map((sw, i) => (
            <Chip key={i} sx={chipSx} label={`${sw.name}${sw.version ? ` (${sw.version})` : ""}`} />
          ))}
        </Box>
      ),
    },
    {
      key: "processes",
      title: "Running AI processes",
      count: signals?.processes?.length || 0,
      render: () => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {signals.processes.map((p, i) => (
            <Typography key={i} sx={rowText}>
              <b>{p.tool}</b> — {p.process_name}
              {p.path ? ` (${p.path})` : ""}
            </Typography>
          ))}
        </Box>
      ),
    },
    {
      key: "vscode",
      title: "VS Code AI extensions",
      count: signals?.vscode_extensions?.length || 0,
      render: () => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {signals.vscode_extensions.map((e, i) => (
            <Typography key={i} sx={rowText}>
              <b>{e.tool}</b>
              {e.latest_version ? ` v${e.latest_version}` : ""} — {e.home}
            </Typography>
          ))}
        </Box>
      ),
    },
    {
      key: "jetbrains",
      title: "JetBrains AI plugins",
      count: signals?.jetbrains_plugins?.length || 0,
      render: () => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {signals.jetbrains_plugins.map((p, i) => (
            <Typography key={i} sx={rowText}>
              <b>{p.tool}</b> — {p.ide} ({p.home})
            </Typography>
          ))}
        </Box>
      ),
    },
    {
      key: "config_dirs",
      title: "AI agent config directories",
      count: signals?.config_dirs?.length || 0,
      render: () => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {signals.config_dirs.map((c, i) => (
            <Typography key={i} sx={rowText}>
              <b>{c.label}</b> — {c.path}
            </Typography>
          ))}
        </Box>
      ),
    },
    {
      key: "browser",
      title: "Browser AI extensions",
      count: signals?.browser_extensions?.length || 0,
      render: () => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {signals.browser_extensions.map((b, i) => (
            <Typography key={i} sx={rowText}>
              <b>{b.label}</b> — {b.browser} ({b.profile})
            </Typography>
          ))}
        </Box>
      ),
    },
    {
      key: "dns",
      title: "AI domains in DNS cache",
      count: signals?.dns_cache?.length || 0,
      render: () => (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {signals.dns_cache.map((d, i) => (
            <Chip key={i} sx={chipSx} label={d.domain} />
          ))}
        </Box>
      ),
    },
    {
      key: "uwp",
      title: "Built-in AI (Windows Store apps)",
      count: signals?.builtin_ai_uwp?.length || 0,
      render: () => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {signals.builtin_ai_uwp.map((u, i) => (
            <Typography key={i} sx={rowText}>
              <b>{u.tool}</b>
              {u.version ? ` v${u.version}` : ""}
            </Typography>
          ))}
        </Box>
      ),
    },
  ];

  const activeSections = sections.filter((s) => s.count > 0);

  return (
    <Box
      sx={{
        border: `1px solid ${isDarkMode ? "#2f2f37" : "#E4E7EC"}`,
        borderRadius: 2,
        mb: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          px: 2,
          py: 1.5,
          backgroundColor: isDarkMode ? "#1f1f26" : "#FAFBFF",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SmartToyOutlinedIcon
            sx={{ fontSize: 20, color: isDarkMode ? "#93C5FD" : "#4A7BF7" }}
          />
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: isDarkMode ? "#FFFFFF" : "#101828",
            }}
          >
            AI Agent Detection
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <StatPill
            label="AI apps installed"
            count={summary?.software_count || 0}
            isDarkMode={isDarkMode}
          />
          <StatPill
            label="Additional signals"
            count={summary?.signal_count || 0}
            isDarkMode={isDarkMode}
          />
        </Box>
      </Box>

      {totalDetections === 0 ? (
        <Box sx={{ px: 2, py: 2 }}>
          <Typography sx={{ fontSize: "13px", color: isDarkMode ? "#9CA3AF" : "#667085" }}>
            No AI agents, coding assistants, or related tools detected on this host.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ px: 1, py: 1 }}>
          {activeSections.map((section) => (
            <Accordion
              key={section.key}
              disableGutters
              elevation={0}
              sx={{
                "&:before": { display: "none" },
                backgroundColor: "transparent",
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon
                    sx={{ color: isDarkMode ? "#9CA3AF" : "#667085" }}
                  />
                }
                sx={{ px: 1, minHeight: "36px" }}
              >
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: isDarkMode ? "#E5E7EB" : "#344054",
                  }}
                >
                  {section.title} ({section.count})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.5, pt: 0, pb: 1.5 }}>
                {section.render()}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default AIAgentSignalsPanel;
